/**
 * Current inbound address with copy, plus the rotated addresses that still
 * work. Shared by the report address (PF-136) and the mailbox copy (#3735).
 */

import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { CopyButton } from '@/components/common/CopyButton';
import type { InboundEmailMailbox } from '@/hooks/useInboundDocuments';
import { formatDateTime } from '@/utils/locale';

interface InboundAddressDetailsProps {
  address: string;
  graceAddresses: readonly InboundEmailMailbox[];
  label: string;
  copyLabel: string;
  testId: string;
}

export function InboundAddressDetails({
  address,
  graceAddresses,
  label,
  copyLabel,
  testId,
}: InboundAddressDetailsProps) {
  const { t } = useTranslation('settings');

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body1" data-testid={testId} sx={{ wordBreak: 'break-all' }}>
            {address}
          </Typography>
          <CopyButton value={address} label={copyLabel} />
        </Stack>
      </Box>

      {graceAddresses.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t('inboundEmail.graceAddresses')}
          </Typography>
          <Stack spacing={0.5}>
            {graceAddresses.map((mailbox) => (
              <Typography key={mailbox.localPart} variant="body2" color="text.secondary">
                {t('inboundEmail.graceAddressItem', {
                  address: mailbox.address,
                  expiresAt: mailbox.expiresAt ? formatDateTime(mailbox.expiresAt) : '',
                })}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
